import { type RefObject, useEffect, useState } from 'react';

const useSticky = <T = HTMLElement>(ref: RefObject<T>, offset: number = 0) => {
  const [isSticky, setIsSticky] = useState(false);
  // High performance scroll listener
  useEffect(() => {
    let animationFrameId: number;

    // Use requestAnimationFrame to sync with the browser's repaint cycle
    const callback = () => {
      const targetElement = ref.current;

      if (!targetElement || !(targetElement instanceof HTMLElement)) {
        return;
      }
      const targetRect = targetElement.getBoundingClientRect();
      const isElementSticky = window.innerHeight - Math.round(targetRect.bottom) <= offset; // Adjust this condition as needed
      setIsSticky(isElementSticky);
    };

    const handleScroll = () => {
      animationFrameId = requestAnimationFrame(() => {
        callback();
      });
    };

    // Run
    callback();

    // Attach the scroll event listener
    window.addEventListener('scroll', handleScroll);

    // Detach the scroll event listener on component unmount
    // Cancel the animation frame to avoid unnecessary updates
    return () => {
      window.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(animationFrameId);
    };
  }, [ref, offset]);

  return { isSticky };
};

export default useSticky;
